import React, { useState, useEffect } from 'react'
import { Shield, Save, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError } from '@/lib/swal'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { useAuthStore } from '@/store/auth.store'
import { gosiService } from '@/services/gosiService'
import type { GosiVm } from '@/api/types/gosi.types'

function defaultGosiForm(branchId?: number): GosiVm {
  return {
    id: 0,
    branchId: branchId ?? 0,
    socialSecurityPercent: 0,
    companyPercent: 0,
    addSocialSecurityPercent: 0,
    addCompanyPercent: 0,
    foreignSocialSecurityPercent: 0,
    foreignCompanyPercent: 0,
    isTransportationAllowance: true,
    isHousingAllowance: true,
    isMealAllowance: true,
    isOtherAllowance: true,
    isAdditionalJob: true,
    minimumWorkingDays: 0,
    maleRetirementAge: 60,
    femaleRetirementAge: 55,
    extendRetirementAgeUpTo: 0,
    useGregorianCalculation: true,
    refundSocialSecurityIntoAccountId: undefined,
    raisesAffectSocialSecuritySalary: true,
    calculateByCalendarDays: true,
    socialSecurityAmountDependsOnWorkedDays: false,
    loadAbsenceContributionToEmployee: false,
    vacationDaysThreshold: 0,
    vacationTypeId: undefined,
    defaultInsuranceLanguage: 'en',
  }
}

export const GOSISetting: React.FC = () => {
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<GosiVm>(() => defaultGosiForm(branchId))

  useEffect(() => {
    if (!branchId) return
    let cancelled = false
    setLoading(true)
    gosiService
      .getGosi(branchId)
      .then((data) => {
        if (cancelled) return
        if (data && (data.id !== 0 || data.socialSecurityPercent != null || data.companyPercent != null)) {
          setForm({ ...defaultGosiForm(branchId), ...data, branchId })
        } else {
          setForm(() => ({ ...defaultGosiForm(branchId), branchId }))
        }
      })
      .catch(() => {
        if (!cancelled) setForm(() => ({ ...defaultGosiForm(branchId), branchId }))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchId])

  const update = (patch: Partial<GosiVm>) => setForm((prev) => ({ ...prev, ...patch }))

  const loadGosiData = async () => {
    if (!branchId) return
    try {
      setLoading(true)
      const data = await gosiService.getGosi(branchId)
      if (data && (data.id !== 0 || data.socialSecurityPercent != null || data.companyPercent != null)) {
        setForm({ ...defaultGosiForm(branchId), ...data, branchId })
      } else {
        setForm(() => ({ ...defaultGosiForm(branchId), branchId }))
      }
    } catch {
      setForm(() => ({ ...defaultGosiForm(branchId), branchId }))
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showSaveError(new Error('Please select a branch first.'))
      return
    }
    setSaving(true)
    try {
      await gosiService.addOrUpdateGosi({ ...form, branchId })
      await showSaveSuccess(false)
      // Refresh form data after successful save
      await loadGosiData()
    } catch (e: any) {
      await showSaveError(e)
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <h1 className="text-3xl font-bold">Gosi setting</h1>
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Please select a branch to manage GOSI settings.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-3xl font-bold">Gosi setting</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Configure GOSI calculation basis, percentages, retirement ages, and allowances
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Card 1: GOSI percentage fields */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                GOSI / Social Security Percentages
              </CardTitle>
              <CardDescription>Currency and contribution percentages</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label>Entry Currency</Label>
                <p className="text-sm text-muted-foreground">Riyal SAR</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="socialSecurityPercent">Social Security Percent (%)</Label>
                  <Input
                    id="socialSecurityPercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.001}
                    value={form.socialSecurityPercent ?? ''}
                    onChange={(e) => update({ socialSecurityPercent: e.target.value ? Number(e.target.value) : 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyPercent">Company Percent (%)</Label>
                  <Input
                    id="companyPercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.001}
                    value={form.companyPercent ?? ''}
                    onChange={(e) => update({ companyPercent: e.target.value ? Number(e.target.value) : 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="addSocialSecurityPercent">Additional Social Security Percent (%)</Label>
                  <Input
                    id="addSocialSecurityPercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.001}
                    value={form.addSocialSecurityPercent ?? ''}
                    onChange={(e) =>
                      update({ addSocialSecurityPercent: e.target.value ? Number(e.target.value) : 0 })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="addCompanyPercent">Company Percent (%)</Label>
                  <Input
                    id="addCompanyPercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.001}
                    value={form.addCompanyPercent ?? ''}
                    onChange={(e) => update({ addCompanyPercent: e.target.value ? Number(e.target.value) : 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="foreignSocialSecurityPercent">Non Saudi Social Percent (%)</Label>
                  <Input
                    id="foreignSocialSecurityPercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.001}
                    value={form.foreignSocialSecurityPercent ?? ''}
                    onChange={(e) =>
                      update({ foreignSocialSecurityPercent: e.target.value ? Number(e.target.value) : 0 })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="foreignCompanyPercent">Company Percent (%)</Label>
                  <Input
                    id="foreignCompanyPercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.001}
                    value={form.foreignCompanyPercent ?? ''}
                    onChange={(e) => update({ foreignCompanyPercent: e.target.value ? Number(e.target.value) : 0 })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Allowances included in calculation + 2 checkboxes */}
          <Card>
            <CardHeader>
              <CardTitle>Allowances Included in Calculation</CardTitle>
              <CardDescription>Select allowances to include in GOSI calculation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.isTransportationAllowance ?? true}
                    onCheckedChange={(c) => update({ isTransportationAllowance: !!c })}
                  />
                  <span>Transportation Allowance</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.isHousingAllowance ?? true}
                    onCheckedChange={(c) => update({ isHousingAllowance: !!c })}
                  />
                  <span>Housing Allowance</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.isMealAllowance ?? true}
                    onCheckedChange={(c) => update({ isMealAllowance: !!c })}
                  />
                  <span>Meal Allowance</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.isAdditionalJob ?? true}
                    onCheckedChange={(c) => update({ isAdditionalJob: !!c })}
                  />
                  <span>Acting Job</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Card 3: Retirement age / working days fields */}
          <Card>
            <CardHeader>
              <CardTitle>Retirement ages</CardTitle>
              <CardDescription>Minimum working days and retirement age settings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minimumWorkingDays">Minimum Working Days</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="minimumWorkingDays"
                      type="number"
                      min={0}
                      step={0.001}
                      value={form.minimumWorkingDays ?? ''}
                      onChange={(e) =>
                        update({ minimumWorkingDays: e.target.value ? Number(e.target.value) : 0 })
                      }
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Days</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maleRetirementAge">Male Retirement Age</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="maleRetirementAge"
                      type="number"
                      min={0}
                      step={0.001}
                      value={form.maleRetirementAge ?? ''}
                      onChange={(e) =>
                        update({ maleRetirementAge: e.target.value ? Number(e.target.value) : 0 })
                      }
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Year</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="femaleRetirementAge">Female Retirement Age</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="femaleRetirementAge"
                      type="number"
                      min={0}
                      step={0.001}
                      value={form.femaleRetirementAge ?? ''}
                      onChange={(e) =>
                        update({ femaleRetirementAge: e.target.value ? Number(e.target.value) : 0 })
                      }
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Year</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="extendRetirementAgeUpTo">Extend Retirement Age Up To Extra</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="extendRetirementAgeUpTo"
                      type="number"
                      min={0}
                      step={0.001}
                      value={form.extendRetirementAgeUpTo ?? ''}
                      onChange={(e) =>
                        update({ extendRetirementAgeUpTo: e.target.value ? Number(e.target.value) : 0 })
                      }
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Years</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
